import React from 'react';
import { message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import FolderSelectorTree from '../../../../../shared/components/FolderSelectorTree';
import { ROOT_DISPLAY_NAME } from '../../../constants';
import FileManagerService from '../../../../../services/FileManager/fileManager.service';
import { CustomFileMap, FileNode, SelectedFolder } from '../../../../../types/FileManagerTypes';

export interface MoveModalProps {
  open: boolean;
  onClose: () => void;
  files?: FileNode[];
  rootFolderId: string;
  currentFolderId?: string;
  moveFiles: (files: FileNode[], destination: Pick<FileNode, 'id'>, source?: FileNode) => boolean | void | Promise<boolean | void>;
}

const ALREADY_IN_FOLDER_MSG = 'Item is already in the selected folder.';
const FOLDER_INTO_SELF_MSG = 'A folder cannot be moved into itself or one of its subfolders.';

const isDescendantOf = (ancestorId: string, nodeId: string, folderMap: CustomFileMap, rootId: string): boolean => {
  let cursor: string | undefined = nodeId;
  const seen = new Set<string>();
  while (cursor && cursor !== rootId && !seen.has(cursor)) {
    if (cursor === ancestorId) return true;
    seen.add(cursor);
    cursor = folderMap[cursor]?.parentId;
  }
  return false;
};

export const MoveModal: React.FC<MoveModalProps> = ({ open, onClose, files = [], rootFolderId, currentFolderId, moveFiles }) => {
  // The browser's fileMap is replaced by search/filter results, so the
  // destination tree self-fetches instead. Same query key, so this shares the
  // tree's request and only backs the validation below.
  const { getFileMapping } = FileManagerService();
  const { data } = useQuery({ ...getFileMapping(open), staleTime: 0 });
  const folderMap: CustomFileMap = data?.fileMap ?? {};

  const handleSelect = (folder: SelectedFolder): boolean => {
    if (!files.length) return false;

    const parentOf = (file: FileNode) => file.parentId ?? rootFolderId;

    if (files.every((file) => parentOf(file) === folder.id)) {
      message.warning(ALREADY_IN_FOLDER_MSG, 3);
      return false;
    }

    const invalidFolderMove = files.some(
      (file) => file.isDir && (file.id === folder.id || isDescendantOf(file.id, folder.id, folderMap, rootFolderId)),
    );
    if (invalidFolderMove) {
      message.warning(FOLDER_INTO_SELF_MSG, 3);
      return false;
    }

    // Fire-and-forget: close the modal immediately; the move runs in the
    // background and VFSBrowser's spinner reflects its progress.
    moveFiles(files, { id: folder.id });
    return true;
  };

  return (
    <FolderSelectorTree
      open={open}
      onClose={onClose}
      onSelect={handleSelect}
      rootFolderId={rootFolderId}
      initialFolderId={currentFolderId}
      variant="dark"
      title="MOVE ITEMS TO"
      bodyLabel="SELECT NEW LOCATION"
      primaryLabel="MOVE"
      rootLabel={`Select ${ROOT_DISPLAY_NAME}`}
    />
  );
};

export default MoveModal;
